import React from 'react';
import styled from 'styled-components';

import { ProductCardTextAlignmentOption } from 'types/graphql';
import { ProductCard } from 'src/dispensary-admin/components/product-card';
import { PreviewData } from '../components/preview';

type ProductCardPreviewProps = {
  data: PreviewData;
  showMobileAppPreview?: boolean;
  showAdvancedCustomization?: boolean;
  // Independent of showAdvancedCustomization (which is coupled to the mobile app section) --
  // strain type pill colors apply to the web marketplace card too, so they're gated on the
  // ecomm.app.advanced-customization flag alone.
  showStrainTypeColors?: boolean;
};

export const ProductCardPreview = ({
  data,
  showMobileAppPreview = false,
  showAdvancedCustomization = false,
  showStrainTypeColors = false,
}: ProductCardPreviewProps): JSX.Element => {
  const { textAlignment, showProductWeight, mobileAppDisplayOptions } = data.productCards;

  // Same on/off surfacing rule as colorOverrides below -- don't keep rendering a color that was
  // saved while the flag was on after it's toggled off.
  const strainTypes = showStrainTypeColors ? data.colors.strainTypes : undefined;

  // Only surface the custom colors (and the other advanced-customization-only options) to the
  // rendered card while the flag is on -- explicitly overriding with undefined (rather than
  // omitting the keys) when it's off, so a value saved while the flag was on doesn't keep
  // rendering/appearing after it's toggled off. Keeps the preview an accurate reflection of what
  // a dispensary actually sees.
  const advancedCustomizationOverrides = showAdvancedCustomization
    ? {
        backgroundColor: mobileAppDisplayOptions.backgroundColor,
        brandColor: mobileAppDisplayOptions.brandColor,
        potencyColor: mobileAppDisplayOptions.potencyColor,
        potencyDisplay: mobileAppDisplayOptions.potencyDisplay,
        priceColor: mobileAppDisplayOptions.priceColor,
        productNameColor: mobileAppDisplayOptions.productNameColor,
        strainTypeDisplay: mobileAppDisplayOptions.strainTypeDisplay,
        strikethroughPriceColor: mobileAppDisplayOptions.strikethroughPriceColor,
        weightColor: mobileAppDisplayOptions.weightColor,
      }
    : {
        backgroundColor: undefined,
        brandColor: undefined,
        potencyColor: undefined,
        potencyDisplay: undefined,
        priceColor: undefined,
        productNameColor: undefined,
        strainTypeDisplay: undefined,
        strikethroughPriceColor: undefined,
        weightColor: undefined,
      };

  let cardImage = '';

  if (textAlignment === ProductCardTextAlignmentOption.center) {
    cardImage = showProductWeight ? 'card-preview-weight-center.png' : 'card-preview-no-weight-center.png';
  } else {
    cardImage = showProductWeight ? 'card-preview-weight-v2.png' : 'card-preview-no-weight.png';
  }

  return (
    <Container>
      <figure>
        <Caption>Card View</Caption>
        <Image src={`https://images.dutchie.com/product-cards/${cardImage}`} alt='Card View Preview' width={184} />
      </figure>
      <figure>
        <Caption>List View</Caption>
        <Image src='https://images.dutchie.com/product-cards/list-preview-v2.png' alt='List View Preview' width={346} />
      </figure>

      {showMobileAppPreview && (
        <figure>
          <Caption>Mobile App Card View</Caption>
          <MobilePreview>
            <MobileCard>
              <ProductCard
                isFullHeight={false}
                strainTypes={strainTypes}
                product={{
                  Name: 'Blue Dream',
                  Prices: [35],
                  Options: ['1g'],
                  brand: { name: 'Dutchie' },
                  type: 'Flower Collection',
                  strainType: 'Hybrid',
                  effects: ['Relaxed'],
                  specialNames: ['Buy More Save More', 'Member Deal'],
                  thcContent: { unit: 'PERCENTAGE', range: [22.1] },
                  cbdContent: { unit: 'PERCENTAGE', range: [0.25] },
                }}
                displayOptions={{
                  ...mobileAppDisplayOptions,
                  ...advancedCustomizationOverrides,
                  showProductWeight: mobileAppDisplayOptions.showProductWeight ?? showProductWeight,
                }}
              />
            </MobileCard>
          </MobilePreview>
        </figure>
      )}
    </Container>
  );
};

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
`;

const Image = styled.img`
  display: block;
  max-width: 100%;
  height: auto;
`;

const Caption = styled.figcaption`
  font-size: 12px;
  line-height: ${18 / 12};
  margin-bottom: 8px;
  text-align: center;
`;

const MobilePreview = styled.div`
  align-items: center;
  background: #f5f8fa;
  border: 1px solid #e3e7e9;
  border-radius: 20px;
  display: flex;
  justify-content: center;
  padding: 12px;
  width: 100%;
`;

const MobileCard = styled.div`
  width: 220px;
`;
